import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import events from '../data/events';

/**
 * Calendar Page
 * P.S. 155 William Paca School Calendar
 * Interactive calendar with month, week, and list views
 */
const Calendar = () => {
  useEffect(() => { document.title = 'Calendar — PS 155 William Paca School'; }, []);
  const [currentView, setCurrentView] = useState('month');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedFilters, setSelectedFilters] = useState({
    holiday: true,
    academic: true,
    event: true,
    meeting: true
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [language, setLanguage] = useState('en');
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);

  const getDaysInMonth = (month, year) => {
    const date = new Date(year, month, 1);
    const days = [];
    const firstDay = new Date(year, month, 1).getDay();
    for (let i = 0; i < firstDay; i++) days.push(null);
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return days;
  };

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getWeekDays = () => {
    const base = new Date(currentYear, currentMonth, 1);
    const start = getStartOfWeek(base);
    const weekStart = new Date(start);
    weekStart.setDate(start.getDate() + currentWeekOffset * 7);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    return { weekStart, days };
  };

  const getFilteredEvents = () => {
    return events.filter(event => selectedFilters[event.category]);
  };

  const getUpcomingEvents = () => {
    const today = new Date();
    return getFilteredEvents()
      .filter(event => event.date >= today)
      .sort((a, b) => a.date - b.date)
      .slice(0, 5);
  };

  const prevMonth = () => {
    if (currentView === 'week') { setCurrentWeekOffset(currentWeekOffset - 1); return; }
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); } else { setCurrentMonth(currentMonth - 1); }
  };

  const nextMonth = () => {
    if (currentView === 'week') { setCurrentWeekOffset(currentWeekOffset + 1); return; }
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); } else { setCurrentMonth(currentMonth + 1); }
  };

  const categoryColors = {
    holiday: 'bg-school-blue',
    academic: 'bg-school-gold',
    event: 'bg-green-700',
    meeting: 'bg-purple-600'
  };

  const categoryTextColors = {
    holiday: 'text-white',
    academic: 'text-school-blue-dark',
    event: 'text-white',
    meeting: 'text-white'
  };

  const getCategoryName = (category) => {
    switch(category) {
      case 'holiday': return 'Holiday';
      case 'academic': return 'Academic';
      case 'event': return 'School Event';
      case 'meeting': return 'Meeting';
      default: return category;
    }
  };

  const getCategoryNameES = (category) => {
    switch(category) {
      case 'holiday': return 'Feriado';
      case 'academic': return 'Académico';
      case 'event': return 'Evento Escolar';
      case 'meeting': return 'Reunión';
      default: return category;
    }
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(date).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', options);
  };

  const getEventTitle = (event) => (language === 'es' && event.titleES ? event.titleES : event.title);
  const getEventDescription = (event) => (language === 'es' && event.descriptionES ? event.descriptionES : (event.description || ''));

  const toggleFilter = (filter) => setSelectedFilters({ ...selectedFilters, [filter]: !selectedFilters[filter] });

  const getEventsForDay = (day) => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getDate() === day.getDate() &&
             eventDate.getMonth() === day.getMonth() &&
             eventDate.getFullYear() === day.getFullYear() &&
             (selectedFilters[event.category] || false);
    });
  };

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  const days = getDaysInMonth(currentMonth, currentYear);
  const upcomingEvents = getUpcomingEvents();
  const closeModal = () => setSelectedEvent(null);

  useEffect(() => {
    if (!selectedEvent) return;
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedEvent]);

  return (
    <div className="min-h-screen bg-neutral-50 font-sans text-neutral-800 selection:bg-school-gold selection:text-white">
      <Navbar />

      <main id="main-content" tabIndex="-1" className="outline-none">
        {/* Hero Section */}
        <section className="relative pt-32 sm:pt-40 md:pt-48 pb-12 sm:pb-14 md:pb-16 overflow-hidden bg-school-blue">
          <div className="absolute inset-0 bg-school-blue-dark"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-block py-1 px-3 rounded-full bg-school-blue text-school-gold-on-blue text-xs sm:text-sm font-bold uppercase tracking-wider mb-3 sm:mb-4 border border-white/10">School Events</span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 tracking-tight">
              School <span className="text-school-gold-on-blue">Calendar</span>
            </h1>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-school-gold mx-auto mb-4 sm:mb-6 rounded-full"></div>
            <p className="text-lg sm:text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto font-light leading-relaxed px-4">
              Stay informed about school events, holidays, and important dates throughout the academic year.
            </p>
          </div>
        </section>

        {/* Calendar Section */}
        <section id="monthly-calendar" className="py-10 sm:py-11 md:py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16 md:mb-20">
              <span className="inline-block bg-school-gold text-school-blue-dark px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-bold uppercase tracking-wider mb-6 sm:mb-8 shadow-lg rounded-full">Interactive Calendar</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-school-blue mb-4 sm:mb-6">Monthly Calendar</h2>
              <p className="text-base sm:text-lg md:text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
                View school events, filter by category, and stay up-to-date with important dates and activities.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8">
              {/* Sidebar */}
              <aside className="lg:col-span-1 space-y-6 sm:space-y-8" aria-label="Calendar filters and options">
                {/* Filters */}
                <div className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-6 sm:p-8 hover:shadow-xl transition-shadow duration-300">
                  <fieldset>
                  <legend className="text-lg sm:text-xl font-bold text-school-blue mb-4 sm:mb-6">Filter Events</legend>
                  <div className="space-y-3" role="group" aria-label="Filter events by category">
                    <div className="flex items-center">
                      <input
                        id="filter-holiday"
                        type="checkbox"
                        checked={selectedFilters.holiday}
                        onChange={() => toggleFilter('holiday')}
                        className="h-4 w-4 sm:h-5 sm:w-5 text-school-blue rounded border-neutral-300 focus:ring-school-blue focus:ring-offset-2"
                      />
                      <label htmlFor="filter-holiday" className="ml-2 sm:ml-3 flex items-center text-neutral-700 text-sm sm:text-base cursor-pointer">
                        <span className="h-3 w-3 bg-school-blue rounded-full mr-2"></span>
                        Holidays
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="filter-academic"
                        type="checkbox"
                        checked={selectedFilters.academic}
                        onChange={() => toggleFilter('academic')}
                        className="h-4 w-4 sm:h-5 sm:w-5 text-school-gold-dark rounded border-neutral-300 focus:ring-school-gold focus:ring-offset-2"
                      />
                      <label htmlFor="filter-academic" className="ml-2 sm:ml-3 flex items-center text-neutral-700 text-sm sm:text-base cursor-pointer">
                        <span className="h-3 w-3 bg-school-gold rounded-full mr-2"></span>
                        Academic Events
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="filter-event"
                        type="checkbox"
                        checked={selectedFilters.event}
                        onChange={() => toggleFilter('event')}
                        className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 rounded border-neutral-300 focus:ring-green-600 focus:ring-offset-2"
                      />
                      <label htmlFor="filter-event" className="ml-2 sm:ml-3 flex items-center text-neutral-700 text-sm sm:text-base cursor-pointer">
                        <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
                        School Events
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="filter-meeting"
                        type="checkbox"
                        checked={selectedFilters.meeting}
                        onChange={() => toggleFilter('meeting')}
                        className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 rounded border-neutral-300 focus:ring-purple-600 focus:ring-offset-2"
                      />
                      <label htmlFor="filter-meeting" className="ml-2 sm:ml-3 flex items-center text-neutral-700 text-sm sm:text-base cursor-pointer">
                        <span className="h-3 w-3 bg-purple-600 rounded-full mr-2"></span>
                        Meetings
                      </label>
                    </div>
                  </div>
                  </fieldset>
                </div>

                {/* Language & View Options */}
                <div className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-6 sm:p-8 hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-lg sm:text-xl font-bold text-school-blue mb-4 sm:mb-6">Language</h3>
                  <div className="flex gap-2 mb-6 sm:mb-8">
                    <button
                      onClick={() => setLanguage('en')}
                      aria-pressed={language === 'en'}
                      className={`flex-1 px-4 py-2 sm:py-2.5 text-sm sm:text-base font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-school-gold ${language === 'en' ? 'bg-school-gold text-school-blue-dark' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}
                    >
                      EN
                    </button>
                    <button
                      onClick={() => setLanguage('es')}
                      aria-pressed={language === 'es'}
                      className={`flex-1 px-4 py-2 sm:py-2.5 text-sm sm:text-base font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-school-gold ${language === 'es' ? 'bg-school-gold text-school-blue-dark' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}
                    >
                      ES
                    </button>
                  </div>

                  <h3 className="text-lg sm:text-xl font-bold text-school-blue mb-4 sm:mb-6">View</h3>
                  <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
                    <button
                      onClick={() => setCurrentView('month')}
                      aria-pressed={currentView === 'month'}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-school-blue ${currentView === 'month' ? 'bg-school-blue text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}
                    >
                      Month
                    </button>
                    <button
                      onClick={() => { setCurrentView('week'); setCurrentWeekOffset(0); }}
                      aria-pressed={currentView === 'week'}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-school-blue ${currentView === 'week' ? 'bg-school-blue text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}
                    >
                      Week
                    </button>
                    <button
                      onClick={() => setCurrentView('list')}
                      aria-pressed={currentView === 'list'}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-school-blue ${currentView === 'list' ? 'bg-school-blue text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`}
                    >
                      List
                    </button>
                  </div>
                  <button
                    onClick={() => { setCurrentMonth(new Date().getMonth()); setCurrentYear(new Date().getFullYear()); setCurrentWeekOffset(0); }}
                    className="w-full py-2.5 sm:py-3 border-2 border-school-blue text-school-blue rounded-lg hover:bg-school-blue hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-school-blue focus:ring-offset-2 font-semibold text-sm sm:text-base"
                  >
                    Today
                  </button>
                </div>

                {/* Upcoming Events */}
                <div className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-6 sm:p-8 hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-lg sm:text-xl font-bold text-school-blue mb-4 sm:mb-6">Upcoming Events</h3>
                  <div className="space-y-4">
                    {upcomingEvents.length > 0 ? (
                      upcomingEvents.map((event) => (
                        <div
                          key={event.id}
                          className="border-l-4 pl-3 sm:pl-4 py-2 cursor-pointer hover:bg-neutral-50 rounded-r transition-colors"
                          style={{
                            borderColor: event.category === 'holiday' ? '#296481' : '#fcbe37'
                          }}
                          onClick={() => setSelectedEvent(event)}
                        >
                          <div className="text-xs sm:text-sm text-neutral-600">{formatDate(event.date)}</div>
                          <div className="font-medium text-neutral-900 text-sm sm:text-base">{getEventTitle(event)}</div>
                          <div className="text-xs sm:text-sm text-neutral-700">All Day</div>
                        </div>
                      ))
                    ) : (
                      <p className="text-neutral-500 italic text-sm">No upcoming events based on your filters.</p>
                    )}
                  </div>
                </div>
              </aside>

              {/* Calendar */}
              <div className="lg:col-span-3" aria-live="polite">
                <div className="bg-white rounded-2xl shadow-lg border border-neutral-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  {/* Calendar Header */}
                  <div className="bg-school-blue text-white px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      {currentView === 'week' ? (
                        (() => {
                          const { days } = getWeekDays();
                          const startLabel = `${days[0].toLocaleString('default', { month: 'short' })} ${days[0].getDate()}`;
                          const endLabel = `${days[6].toLocaleString('default', { month: 'short' })} ${days[6].getDate()}, ${days[6].getFullYear()}`;
                          return <h3 className="text-base sm:text-lg md:text-xl font-bold truncate">Week of {startLabel} – {endLabel}</h3>;
                        })()
                      ) : (
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold">{monthNames[currentMonth]} {currentYear}</h3>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={prevMonth}
                        className="p-2 rounded-full hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-school-blue"
                        aria-label="Previous"
                      >
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <button
                        onClick={nextMonth}
                        className="p-2 rounded-full hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-school-blue"
                        aria-label="Next"
                      >
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Day Headers */}
                  <div className="grid grid-cols-7 text-center bg-neutral-50 border-b border-neutral-200">
                    {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((day, i) => (
                      <div key={i} className="py-2 sm:py-3 font-semibold text-school-blue text-xs sm:text-sm md:text-base">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Month View */}
                  {currentView === 'month' && (
                    <div className="grid grid-cols-7 border-b border-neutral-200 min-h-[400px] sm:min-h-[500px] md:min-h-[600px]">
                      {days.map((day, index) => {
                        const dayEvents = day ? getEventsForDay(day) : [];
                        const isToday = day && day.toDateString() === new Date().toDateString();
                        return (
                          <div
                            key={index}
                            className={`border-t border-l p-1 sm:p-2 h-20 sm:h-24 md:h-32 overflow-hidden ${
                              day ? 'bg-white' : 'bg-neutral-50 text-neutral-400'
                            } ${
                              isToday ? 'bg-school-gold/5 ring-2 ring-inset ring-school-gold' : ''
                            } ${
                              index % 7 === 6 ? 'border-r' : ''
                            }`}
                          >
                            {day && (
                              <>
                                <div className={`text-right mb-1 text-xs sm:text-sm md:text-base ${isToday ? 'font-bold text-school-gold-dark' : 'text-neutral-700'}`}>
                                  {day.getDate()}
                                </div>
                                <div className="space-y-1 overflow-y-auto max-h-[50px] sm:max-h-[60px] md:max-h-[80px]">
                                  {dayEvents.map((event) => (
                                    <button
                                      key={event.id}
                                      onClick={() => setSelectedEvent(event)}
                                      className={`text-[9px] sm:text-[10px] md:text-xs w-full text-left px-1 sm:px-2 py-0.5 sm:py-1 rounded ${categoryTextColors[event.category]} ${categoryColors[event.category]} hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-school-blue truncate`}
                                      title={getEventTitle(event)}
                                      aria-label={`Event: ${getEventTitle(event)}`}
                                    >
                                      {getEventTitle(event)}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Week View */}
                  {currentView === 'week' && (() => {
                    const { days: weekDays } = getWeekDays();
                    return (
                      <div className="border-b border-neutral-200 min-h-[400px]">
                        <div className="grid grid-cols-7 text-center bg-neutral-50 border-b border-neutral-200">
                          {weekDays.map((d, i) => (
                            <div key={i} className="py-2 sm:py-3 font-medium text-school-blue">
                              <div className="text-sm sm:text-base">{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]}</div>
                              <div className="text-xs text-neutral-600">{d.toLocaleString('default', { month: 'short' })} {d.getDate()}</div>
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 auto-rows-fr min-h-[320px]">
                          {weekDays.map((day, index) => {
                            const dayEvents = getEventsForDay(day);
                            const isToday = day.toDateString() === new Date().toDateString();
                            return (
                              <div
                                key={index}
                                className={`border-t border-l p-2 sm:p-3 ${
                                  index % 7 === 6 ? 'border-r' : ''
                                } ${
                                  isToday ? 'bg-school-gold/5 ring-2 ring-inset ring-school-gold' : 'bg-white'
                                }`}
                              >
                                <div className={`text-right mb-2 text-sm sm:text-base ${isToday ? 'font-bold text-school-gold-dark' : ''}`}>
                                  {day.getDate()}
                                </div>
                                <div className="space-y-2 overflow-y-auto max-h-[260px]">
                                  {dayEvents.length === 0 && (
                                    <div className="text-xs text-neutral-400 italic">{language === 'es' ? 'Sin eventos' : 'No events'}</div>
                                  )}
                                  {dayEvents.map((event) => (
                                    <button
                                      key={event.id}
                                      onClick={() => setSelectedEvent(event)}
                                      className={`text-xs w-full text-left px-2 py-1 rounded ${categoryTextColors[event.category]} ${categoryColors[event.category]} hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-school-blue`}
                                      title={getEventTitle(event)}
                                      aria-label={`Event: ${getEventTitle(event)}`}
                                    >
                                      <span className="truncate block">{getEventTitle(event)}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* List View */}
                  {currentView === 'list' && (
                    <div className="border-b border-neutral-200 min-h-[400px] sm:min-h-[600px] p-4 sm:p-6">
                      <div className="space-y-4">
                        {getFilteredEvents()
                          .filter(event => event.date.getMonth() === currentMonth && event.date.getFullYear() === currentYear)
                          .sort((a, b) => a.date - b.date)
                          .map(event => (
                            <div
                              key={event.id}
                              className="flex border-l-4 p-4 bg-neutral-50 rounded-r hover:bg-neutral-100 transition-colors cursor-pointer"
                              style={{
                                borderColor: event.category === 'holiday' ? '#296481' : '#fcbe37'
                              }}
                              onClick={() => setSelectedEvent(event)}
                            >
                              <div className="mr-4 sm:mr-6 text-center flex-shrink-0">
                                <div className="font-bold text-lg sm:text-xl text-school-blue">{event.date.getDate()}</div>
                                <div className="text-xs text-neutral-600">{event.date.toLocaleString('default', { month: 'short' })}</div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-neutral-900 text-base sm:text-lg mb-1">{getEventTitle(event)}</div>
                                <div className="text-sm text-neutral-700 mb-2">All Day</div>
                                <div className="text-sm text-neutral-600 line-clamp-2">{getEventDescription(event)}</div>
                                <div className="mt-2">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    event.category === 'holiday' ? 'bg-school-blue/10 text-school-blue' : 'bg-school-gold/10 text-school-gold-dark'
                                  }`}>
                                    {language === 'es' ? getCategoryNameES(event.category) : getCategoryName(event.category)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="event-modal-title">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/50 transition-opacity" aria-hidden="true" onClick={closeModal}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className={`${categoryColors[selectedEvent.category]} py-6 sm:py-8 px-6 sm:px-8`}>
                <h3 id="event-modal-title" className={`text-xl sm:text-2xl font-bold ${categoryTextColors[selectedEvent.category]}`}>{getEventTitle(selectedEvent)}</h3>
                <p className={`mt-2 text-base sm:text-lg ${selectedEvent.category === 'academic' ? 'text-school-blue-dark/90' : 'text-white/90'}`}>{formatDate(selectedEvent.date)}</p>
              </div>
              <div className="bg-white px-6 sm:px-8 py-6">
                <div className="text-neutral-600">
                  <p className="mb-2 text-base sm:text-lg leading-relaxed">{getEventDescription(selectedEvent)}</p>
                  {selectedEvent.allDay && (
                    <div className="flex items-center mb-3 mt-4">
                      <svg className="h-5 w-5 text-neutral-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path fillRule="evenodd" d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3a.75.75 0 011.5 0v1.5h.75A2.25 2.25 0 0121 6.75v10.5A2.25 2.25 0 0118.75 19.5H5.25A2.25 2.25 0 013 17.25V6.75A2.25 2.25 0 015.25 4.5H6V3a.75.75 0 01.75-.75zM4.5 9h15v8.25a.75.75 0 01-.75.75H5.25a.75.75 0 01-.75-.75V9z" clipRule="evenodd" />
                      </svg>
                      <span>{language === 'es' ? 'Todo el día' : 'All Day'}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-neutral-50 px-6 sm:px-8 py-4 flex justify-end">
                <button
                  type="button"
                  className="px-6 py-2.5 sm:py-3 bg-white border-2 border-neutral-300 rounded-lg text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-school-blue transition-colors"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Calendar;
